/**
 * two-zone-v2 stylesheet generator (Spec 340).
 *
 * Pure, deterministic: maps a {@link ThemeKnobs} value + the service's current
 * background URL into a complete two-zone-v2 stylesheet string, with the
 * `@theme-knobs` round-trip header on top. Runs client-side — no BFF endpoint.
 *
 * Key invariants (spec "Traps to Avoid"):
 * - The `background-image: url()` is always written from the passed `backgroundUrl`
 *   (stage-correct), never copied from old CSS — this kills the cross-stage CSP trap.
 * - Colours are normalised to canonical `#RRGGBB` before interpolation; invalid
 *   values fall back to defaults, so no untrusted string reaches the CSS.
 * - The header is always written fresh from the template; we never parse CSS to write.
 */
import {
  type ThemeKnobs,
  DEFAULT_KNOBS,
  normalizeHex,
  clampRadius,
} from './theme-knobs';
import { serializeKnobs } from './theme-knobs-header';
import { buildTwoZoneV2Css, type TwoZoneV2TemplateParts } from './two-zone-v2-template';

export interface GenerateOptions {
  /** The service's current background image URL (stage-correct). Empty/invalid → no bg-image. */
  backgroundUrl?: string | null;
}

/** Convert a canonical `#RRGGBB` to its r/g/b components. */
function hexToRgb(hex: string): { r: number; g: number; b: number } {
  return {
    r: parseInt(hex.slice(1, 3), 16),
    g: parseInt(hex.slice(3, 5), 16),
    b: parseInt(hex.slice(5, 7), 16),
  };
}

/** Glow ring colour derived from the CTA fill: same hue, fixed 0.75 alpha (matches the real two-zone-v2 glow). */
function glowColor(ctaColor: string): string {
  const { r, g, b } = hexToRgb(ctaColor);
  return `rgba(${r},${g},${b},0.75)`;
}

/**
 * Build a safe `background-image` declaration from a URL, or '' if the URL is
 * missing/not an https URL. Whitespace/control chars are rejected and quotes /
 * backslashes escaped — defence against breaking out of the `url("…")` context.
 */
function backgroundImageDecl(url: string | null | undefined): string {
  if (typeof url !== 'string') return '';
  const trimmed = url.trim();
  if (!trimmed.startsWith('https://')) return '';
  // Valid CDN URLs contain no whitespace/control characters; reject if any are present.
  for (let i = 0; i < trimmed.length; i++) {
    const code = trimmed.charCodeAt(i);
    if (code <= 0x20) return '';
  }
  const safe = trimmed.replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  return `background-image: url("${safe}") !important;`;
}

/** Solid or gradient CTA fill, with a solid `background-color` fallback. */
function ctaBackgroundDecl(ctaColor: string, ctaGradientTo: string | null): string {
  const fill = ctaGradientTo
    ? `background: linear-gradient(180deg, ${ctaColor} 0%, ${ctaGradientTo} 100%) !important;`
    : `background: ${ctaColor} !important;`;
  return `${fill}\nbackground-color: ${ctaColor} !important;`;
}

/** CTA box-shadow: depth + inset always; the glow ring only when `glow` is on. */
function ctaBoxShadow(ctaColor: string, glow: boolean): string {
  const depth = '0 10px 24px rgba(0,0,0,0.50), inset 0 2px 0 rgba(255,255,255,0.35)';
  return glow ? `0 0 34px ${glowColor(ctaColor)}, ${depth}` : depth;
}

/**
 * Normalise possibly-untrusted knobs into safe values (canonical hex, clamped
 * radius). Invalid colours fall back to defaults rather than reaching the CSS.
 */
function safeKnobs(knobs: ThemeKnobs): ThemeKnobs {
  const ctaColor = normalizeHex(knobs.ctaColor) ?? DEFAULT_KNOBS.ctaColor;
  const ctaGradientTo =
    knobs.ctaGradientTo == null ? null : normalizeHex(knobs.ctaGradientTo);
  return {
    v: 1,
    ctaColor,
    ctaGradientTo,
    glow: !!knobs.glow,
    radius: clampRadius(knobs.radius),
  };
}

/**
 * Generate a complete two-zone-v2 stylesheet from knobs + the current background URL.
 * Output begins with the `@theme-knobs` header (re-readable via `parseKnobsFromCss`).
 */
export function generateTwoZoneV2(knobs: ThemeKnobs, opts: GenerateOptions = {}): string {
  const safe = safeKnobs(knobs);

  const parts: TwoZoneV2TemplateParts = {
    backgroundImageDecl: backgroundImageDecl(opts.backgroundUrl),
    ctaBackgroundDecl: ctaBackgroundDecl(safe.ctaColor, safe.ctaGradientTo),
    ctaBorderRadiusPx: safe.radius,
    ctaBoxShadow: ctaBoxShadow(safe.ctaColor, safe.glow),
  };

  const header = serializeKnobs(safe);
  return `${header}\n\n${buildTwoZoneV2Css(parts)}`;
}
